import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseApi, RequestType } from '@kapi';
import { EnvironmentVariablesService } from '@kenv';
import { DialogueBlockEvent } from '@kp/dialogue/models/dialogue-block-event.model';
import { DialogueDataStatus } from '@kp/dialogue/models/dialogue-data-status.model';
import { Dialogue, DialogueLogicKey, KumanuKeyType } from '@kp/dialogue/models/dialogue.model';
import { Constants } from '@kp/shared/constants.service';
import { DataStoreService } from '@kservice';
import { JsonObject } from '@ktypes/models';
import { DateTimeUtil } from '@kutil';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DialogueApi extends BaseApi {
  constructor(
    private _client: HttpClient,
    dataStoreService: DataStoreService,
    private _environmentVariablesService: EnvironmentVariablesService
  ) {
    super(_client, dataStoreService, _environmentVariablesService);
  }

  async createDialogueSession(
    dialogueLogicKey: DialogueLogicKey,
    keyType: KumanuKeyType,
    refKey?: string,
    showPreviousAnswers = false
  ): Promise<Dialogue> {
    const requestBody: JsonObject = {
      logicKey: dialogueLogicKey,
      systemKey: Constants.dialogueSystemKey,
      deviceCreatedTimestamp: DateTimeUtil.formatInLocal(),
    };
    if (keyType === KumanuKeyType.refKey) {
      requestBody['refKey'] = refKey;
    }
    if (showPreviousAnswers) {
      requestBody['showPreviousAnswers'] = true;
    }
    const request$ = this.performRequest<Dialogue>(RequestType.POST, this.buildUrl('/dialogue', true), {
      includeToken: true,
      requestBody,
    }).pipe(
      map(
        (response: HttpResponse<Dialogue>): Dialogue =>
          response?.ok ? new Dialogue().deserialize({ ...(response?.body || {}), refKey }) : null
      ),
      catchError(handleObservableError('Failed creating dialogue session: '))
    );
    return firstValueFrom(request$).catch(handleError('Error creating dialogue session: '));
  }

  async getDialogue(dialogueId: string): Promise<Dialogue> {
    const request$ = this.performRequest<Dialogue>(RequestType.GET, this.buildUrl(`/dialogue/${dialogueId}`, true), {
      includeToken: true,
    }).pipe(
      map(
        (response: HttpResponse<Dialogue>): Dialogue =>
          response?.ok ? new Dialogue().deserialize(response?.body) : null
      ),
      catchError(handleObservableError('Failed getting dialogue session: '))
    );
    return firstValueFrom(request$).catch(handleError('Error getting dialogue session: '));
  }

  async createDialogueBlockEvent(
    event: DialogueBlockEvent,
    dialogueId: string,
    dialogueBlockId: string
  ): Promise<Dialogue> {
    const request$ = this.performRequest<Dialogue>(
      RequestType.POST,
      this.buildUrl(`/dialogue/${dialogueId}/block/${dialogueBlockId}/event`, true),
      {
        includeToken: true,
        requestBody: event.serialize(),
      }
    ).pipe(
      map((response: HttpResponse<JsonObject>) =>
        response?.ok ? new Dialogue().deserialize(response?.body?.userDialogue) : null
      ),
      catchError(handleObservableError('Failed creating dialogue event: '))
    );
    return firstValueFrom(request$).catch(handleError('Error creating dialogue event: '));
  }

  async getDialogueStatus(logicKey: DialogueLogicKey): Promise<DialogueDataStatus> {
    const params = {
      logicKey,
      systemKey: Constants.dialogueSystemKey,
      deviceCreatedTimestamp: DateTimeUtil.formatInLocal(),
    };
    const request$ = this.performRequest<DialogueDataStatus>(
      RequestType.GET,
      this.buildUrl('/dialogue/status', true, params),
      {
        includeToken: true,
      }
    ).pipe(
      map((response: HttpResponse<DialogueDataStatus>) =>
        response?.ok ? new DialogueDataStatus().deserialize(response?.body) : null
      ),
      catchError(handleObservableError('Failed getting dialogue status: '))
    );
    return firstValueFrom(request$).catch(handleError('Error getting dialogue status: '));
  }
}

function handleObservableError(errorMessage: string) {
  return (error) => {
    console.warn(errorMessage, error);
    return of(null);
  };
}

function handleError(errorMessage: string) {
  return (error) => {
    console.warn(errorMessage, error);
    return null;
  };
}
